import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { finalize, Subscription, tap } from 'rxjs';
import { LogTask } from 'src/app/model/LogTask';
import { SolutionInfo } from 'src/app/model/SolutionInfo';
import { EventService } from 'src/app/service/event.service';
import { LogTaskResponse, SupportService } from 'src/app/service/support.service';
import { Events } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { ActionMenuEvent, ActionMenuIcon, ActionMenuItem } from '../controls/actionmenu/action-menu.component';
import { ColumnType, DataTableColumn, DataTableFilter, DataTableRow } from '../controls/datatable/datatable.component';
import { ModalMessageComponent, ModalMessageType } from '../controls/modal-message/modal-message.component';
import { Modal } from '../controls/modal/modal.service';
import { PageCount, PageEvent } from '../controls/pagination/pagination.component';

export enum LogPackageField {
  ID = "id",
  NAME = "prefix",
  SOLUTION = "solName",
  ENVIRONMENT = "envName",
  REQUESTBY = "initiatorUserName",
  CREATED = "createdDateTime",
  LASTUPDATED = "updatedDateTime",
  SIZE = "fileSize",
  STATUS = "status",
  DOWNLOAD = "downloadUrl",
  UPLOADSTATUS = "uploadStatus",
}

@Component({
    selector: 'app-settings-support',
    templateUrl: './settings-support.component.html',
    styleUrls: ['./settings-support.component.css'],
    standalone: false
})
export class SettingsSupportComponent implements OnInit {

  title: string = $localize`Log Packages`;
  cols: DataTableColumn[] = [];
  filters: DataTableFilter[] = [];
  rows: DataTableRow[] = [];
  total: number = 0;
  isInit: boolean = true;

  logTaskResponse: LogTaskResponse;

  solutionInfo: SolutionInfo;
  loading: boolean = false;
  packagingLogs: boolean = true;

  pageInfo: PageEvent;

  solutionInfoSubscription: Subscription;
  eventMessageSubscription: Subscription;
  routerSubscription: Subscription;

  constructor(protected router: Router,
    public route: ActivatedRoute,
    private modal: Modal,
    private eventService: EventService,
    private supportService: SupportService) {

    this.cols = Array<DataTableColumn>(
      new DataTableColumn(LogPackageField.NAME, $localize`Name`, ColumnType.STRING, false, "13"),
      new DataTableColumn(LogPackageField.SOLUTION, $localize`Solution`, ColumnType.STRING, false, "13"),
      new DataTableColumn(LogPackageField.ENVIRONMENT, $localize`Environment`, ColumnType.STRING, false, "13"),
      new DataTableColumn(LogPackageField.REQUESTBY, $localize`Requested By`, ColumnType.STRING, false, "10"),
      new DataTableColumn(LogPackageField.CREATED, $localize`Created`, ColumnType.DATEFROMNOW, false, "12"),
      new DataTableColumn(LogPackageField.LASTUPDATED, $localize`Updated`, ColumnType.DATEFROMNOW, false, "12"),
      new DataTableColumn(LogPackageField.STATUS, $localize`Status`, ColumnType.STATUSEXT, false, "6"),
      new DataTableColumn(LogPackageField.SIZE, $localize`Size`, ColumnType.FILESIZE, false, "8"),
      new DataTableColumn(LogPackageField.UPLOADSTATUS, $localize`Upload`, ColumnType.STATUSEXT, false, "7"),
      new DataTableColumn(LogPackageField.DOWNLOAD, $localize`URL`, ColumnType.DOWNLOADLINK, false, "6")
    );
  }

  ngOnInit(): void {
    this.pageInfo = new PageEvent(1, 0, PageCount.TWENTYFIVE);
    this.solutionInfoSubscription = this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      this.solutionInfo = newSolutionInfo;
      if (this.isInit){
        this.isInit = false;
        setTimeout(() => {
          this.getLogTasks();
        }, 100);
      }
    });
    this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
      if (msg.eventType == Events.ActivityRefresh) {
        if (msg.body !== undefined && msg.body.type == "PACKAGE_LOGS"){
          this.refreshTask();
        }
      }
    });

    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if(this.router.routerState.snapshot.url.indexOf("/support/logs") == -1){
          setTimeout(() => {
            this.getLogTasks();
          }, 100);
        }
      }
    });
  }

  ngOnDestroy() {
    this.solutionInfoSubscription.unsubscribe();
    this.eventMessageSubscription.unsubscribe();
    this.routerSubscription.unsubscribe();
  }

  getLogTasks(): void {
    this.loading = true;
    this.supportService.getLogTasks(this.pageInfo.itemsPerPage, this.pageInfo.startingIndex).pipe(
      tap(resp => {
        this.logTaskResponse = resp.body;
        this.total = this.logTaskResponse.count;

        var tmpRows: DataTableRow[] = [];
        this.logTaskResponse.tasks.forEach(task => {
          tmpRows.push(this.setTaskRow(task));
        });

        this.rows = tmpRows;
      }),
      finalize(() => {
        this.loading = false;
      })
    ).subscribe();
  }

  setTaskRow(task: any): DataTableRow {
    let currentStatus = task.status;
    let row: DataTableRow;
    task.status = Utils.getActivityStatus(currentStatus);
    task.downloadUrl = (currentStatus == 'COMPLETED') ? this.logTaskResponse.downloadURL + '/' + task.uuid : "";
    task.uploadStatus = task.isUploaded ? { className: 'otsc-icon-status-acknowledged', altText: $localize`Uploaded` } : { className: 'otsc-icon-status-acknowledged-not-required', altText: $localize`Upload pending` };

    if (currentStatus == "QUEUED" || currentStatus == "RUNNING") {
      row = new DataTableRow(task, []);
      row.disableCheckbox = true;
    } else {
      row = new DataTableRow(task, [new ActionMenuItem("Delete", $localize`Delete`, ActionMenuIcon.DELETE, task, true)]);
      if (currentStatus == 'COMPLETED') {
        row.actions.push(new ActionMenuItem("Upload", $localize`Upload`, ActionMenuIcon.UPLOAD, task, false));
      }
    }

    return row;
  }

  refreshTask(): void{
    this.supportService.getLogTasks(this.pageInfo.itemsPerPage, this.pageInfo.startingIndex).pipe(
      tap(resp => {
        this.logTaskResponse = resp.body;
        this.total = this.logTaskResponse.count;
        this.logTaskResponse.tasks.forEach(task => {
          this.setTask(this.setTaskRow(task));
        });
      })
    ).subscribe();
  }

  setTask(row: DataTableRow): void{
    for(let i = 0; i < this.rows.length; i++){
      if (this.rows[i].data.uuid == row.data.uuid && this.rows[i].data.status != row.data.status){
        this.rows[i] = row;
        break;
      }
    }
  }

  addLogsTask(): void {
    this.packageLogs();
  }

  packageLogs(): void {
    this.router.navigate(["/settings/support/logs"]);
  }

  onPageChange(pageEvent: PageEvent) {
    this.pageInfo = pageEvent
    this.getLogTasks();
  }


  actionMenuClicked(event: ActionMenuEvent): void {
    switch (event.name) {
      case "Delete":
        this.deleteLogs(event.data);
        break;
      case "Upload":
        this.uploadLogs(event.data);
        break;
    }
  }

  deleteLogs(tasks: LogTask[]) {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Delete`,
          type: ModalMessageType.Question,
          message: tasks.length > 1 ? $localize`Depending on the date and time these packages were created the information contained within the logs may no longer be available. Are you sure you want to delete these ${tasks.length} packages?` :
            $localize`Depending on the date and time this package was created the information contained within the logs may no longer be available. Are you sure you want to delete this package?`,
          primaryBtn: $localize`Delete`,
          secondaryBtn: $localize`Cancel`,
          width: '550px'
        },
        id: 'otsc-delete-log-task-msg'
      }).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          let success: boolean = false;
          this.loading = true;
          let taskids: string[] = [];
          tasks.forEach(task =>{
            taskids.push(task.uuid);
          });
          this.supportService.deleteTasks(taskids, this.solutionInfo.currentEnvironment.id).pipe(
            tap(resp => {
              success = true;
              this.getLogTasks();
            }),
            finalize(() => {
              if (!success) {
                this.loading = false;
              }
            })
          ).subscribe();
        }
        sub.unsubscribe();
      });
  }

  uploadLogs(tasks: LogTask[]) {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Upload Log Package`,
          type: ModalMessageType.Question,
          message: $localize`Before uploading the selected log package, verify that System Center Manager has internet access and you've opened a support case for files over 25 MB or generated an SFTP transfer.`,
          primaryBtn: $localize`Upload`,
          secondaryBtn: $localize`Cancel`,
          width: '550px'
        },
        id: 'otsc-upload-log-task-msg'
      }).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.router.navigate(['/settings/support/logs'], { state: { selectedLogsPackageId: tasks[0].uuid } });
        }
        sub.unsubscribe();
      });
  }
}
